import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { Box, Stack } from "@mui/system";

import { checkPasswordComplexity } from "check-password-complexity";

import classNames from "classnames";

import { Modal } from "@/components";
import { useChangePasswordMutation } from "@/pages/Private/redux/account/account.api";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { ChangePassword, ChangePasswordSchema } from "../schema/account";

interface ChangePasswordModalProps {
	isVisible: boolean;
	handleClose: () => void;
}

export const ChangePasswordModal: FunctionComponent<ChangePasswordModalProps> = ({
	isVisible,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);
	const [passwordComplexity, setPasswordComplexity] = useState<number>(0);

	const [changePassword, { isLoading, error }] = useChangePasswordMutation();

	const {
		register,
		handleSubmit,
		setValue,
		watch,

		formState: { errors },
		reset,
	} = useForm<ChangePassword>({
		defaultValues: {
			oldPassword: "",
			password: "",
			confirmPassword: "",
		},
		resolver: zodResolver(ChangePasswordSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: ChangePassword) => {
		try {
			await changePassword(values).unwrap();
			reset();
			handleClose();
		} catch (err) {
			console.error(err);
		}
	};

	const formValues = watch();

	useEffect(() => {
		if (formValues.password) {
			const complexity = checkPasswordComplexity(formValues.password);

			setPasswordComplexity(complexity.score);
		} else {
			setPasswordComplexity(0);
		}
	}, [formValues.password]);

	const passwordIncludesNumbers = useMemo(() => {
		return formValues.password.match(/\d/);
	}, [formValues.password]);

	const passwordIncludesSpecialCharacters = useMemo(() => {
		return formValues.password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/);
	}, [formValues.password]);

	const passwordIncludesUppercase = useMemo(() => {
		return formValues.password.match(/[A-Z]/);
	}, [formValues.password]);

	const passwordIncludesLowercase = useMemo(() => {
		return formValues.password.match(/[a-z]/);
	}, [formValues.password]);

	const tileColor = useMemo(() => {
		if (passwordComplexity >= 5) {
			return "bg-status-success";
		} else if (passwordComplexity >= 3) {
			return "bg-status-warning";
		} else if (passwordComplexity >= 1) {
			return "bg-status-error";
		}

		return "bg-gray-200";
	}, [passwordComplexity]);

	return (
		<Modal
			disableSave={passwordComplexity < 5}
			handleClose={handleClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isVisible}
			title={ts("changePassword")}
		>
			<Box component="form" onSubmit={handleSubmit(onSubmit)}>
				<Stack mt="2" spacing={2}>
					<TextField
						error={!!errors.oldPassword?.message}
						label={ts("currentPassword")}
						placeholder={ts("currentPassword")}
						type="password"
						{...register("oldPassword")}
					/>

					<TextField
						error={!!errors.password?.message}
						label={ts("newPassword")}
						placeholder={ts("newPassword")}
						type="password"
						{...register("password")}
						onChange={(e) => {
							setValue("password", e.target.value);
						}}
					/>
					{formValues.password && formValues.password?.length > 0 && (
						<>
							<div className="flex flex-row gap-2">
								<div
									className={classNames(
										"w-1/3 h-[3px] bg-gray-200 rounded-md",
										passwordComplexity >= 1 ? tileColor : "bg-gray-200"
									)}
								></div>
								<div
									className={classNames(
										"w-1/3 h-[3px] bg-gray-200 rounded-md",
										passwordComplexity >= 3 ? tileColor : "bg-gray-200"
									)}
								></div>
								<div
									className={classNames(
										"w-1/3 h-[3px] bg-gray-200 rounded-md",
										passwordComplexity >= 5 ? tileColor : "bg-gray-200"
									)}
								></div>
							</div>

							<div className="text-left text-xs font-normal w-full mb-4">
								{formValues.password?.length < 8 && (
									<div className="text-left text-xs font-normal w-full mb-1">
										{t("basics.passwordGuidelines.minimum")}
									</div>
								)}
								{!passwordIncludesNumbers && (
									<div className="text-left text-xs font-normal w-full mb-1">
										{t("basics.passwordGuidelines.number")}{" "}
									</div>
								)}
								{!passwordIncludesSpecialCharacters && (
									<div className="text-left text-xs font-normal w-full mb-1">
										{t("basics.passwordGuidelines.specialChar")}{" "}
									</div>
								)}
								{!passwordIncludesUppercase && (
									<div className="text-left text-xs font-normal w-full mb-1">
										{t("basics.passwordGuidelines.uppercase")}{" "}
									</div>
								)}
								{!passwordIncludesLowercase && (
									<div className="text-left text-xs font-normal w-full mb-1">
										{t("basics.passwordGuidelines.lowercase")}{" "}
									</div>
								)}
							</div>
						</>
					)}

					<TextField
						error={!!errors.confirmPassword?.message}
						label={ts("repeatPassword")}
						placeholder={ts("repeatPassword")}
						type="password"
						{...register("confirmPassword")}
					/>

					{getAllErrors(error, formErrors).length
						? renderErrorMessages(getAllErrors(error, formErrors))
						: null}
				</Stack>
			</Box>
		</Modal>
	);
};
